import { TYPE } from '@felicity/primitives';
import { Component, EVENT } from '@felicity/vision';
import { Breadcrumb } from '@syncfusion/ej2-navigations';

export default class SlidingPanelLayout extends Component {
  /**
   * Provides access to the breadcrumb navigation
   * @type {Breadcrumb}
   * @readonly
   */
  get breadcrumb() {
    return this.#breadcrumb;
  }

  /**
   * The panels contained in this layout
   * @type {Array<HTMLElement>}
   * @readonly
   */
  get panels() {
    return this.#panels;
  }

  /** @type {Breadcrumb} */
  #breadcrumb;

  /** @type {Array<HTMLElement>} */
  #panels = [];

  /** @type {HTMLElement} */
  #panelContainer;

  addPanel(header, content) {
    const panel = document.createElement('div');
    panel.classList.add('panel');
    panel.classList.add('hidden');
    this.#panelContainer.appendChild(panel);

    const panelHeader = document.createElement('div');
    panelHeader.classList.add('panel-header');
    panelHeader.textContent = header || `Panel ${this.panels.length + 1}`;
    panel.appendChild(panelHeader);

    const panelContent = document.createElement('div');
    panelContent.classList.add('panel-content');
    panel.appendChild(panelContent);

    if (content instanceof HTMLElement) {
      panelContent.appendChild(content);
    } else if (typeof content === TYPE.STRING) {
      panelContent.innerHTML = content;
    }

    this.#panels.push(panel);

    if (this.panels.length === 1) {
      this.showPanel(0);
    }
    return panel;
  }

  showPanel(index) {
    if (index < 0 || index >= this.panels.length) {
      throw new Error('Panel index out of bounds');
    }

    this.panels.forEach((panel, i) => {
      if (i === index) {
        panel.classList.remove('hidden');
        if (i > 0) {
          panel.classList.add('shifted');
        } else {
          panel.classList.remove('shifted');
        }
      } else if (i > index) {
        panel.classList.add('hidden');
        panel.classList.remove('shifted');
      } else {
        panel.classList.remove('hidden');
        panel.classList.remove('shifted');
      }

      panel.querySelectorAll('.flc-component')
        .forEach((component) => {
          if (typeof component.enable === TYPE.FUNCTION) {
            component.enable(i === index);
          }
        });
    });

    if (index > this.breadcrumb.items.length - 1) {
      this.breadcrumb.items.push({
        id: `panel-${index}`,
        text: this.panels[index].querySelector('.panel-header').textContent,
        index
      });
    } else {
      this.breadcrumb.selectedItem = this.breadcrumb.items[index];
    }

    this.breadcrumb.refresh();
  }

  removePanel(index) {
    if (index < 0 || index >= this.panels.length) {
      throw new Error('Panel index out of bounds');
    }

    const panel = this.panels[index];
    this.#panelContainer.removeChild(panel);
    this.#panels.splice(index, 1);

    this.breadcrumb.items.splice(index + 1, 1);
    this.breadcrumb.refresh();
  }

  clear() {
    for (let i = this.panels.length - 1; i > 0; i--) {
      this.removePanel(i);
    }
  }

  _prepare() {
    super._prepare();

    this.classList.add('sliding-panel-layout');

    const breadcrumbContainer = document.createElement('div');
    breadcrumbContainer.classList.add('breadcrumb');
    this.appendChild(breadcrumbContainer);

    this.#panelContainer = document.createElement('div');
    this.#panelContainer.classList.add('panel-container');
    this.appendChild(this.#panelContainer);

    this.#breadcrumb = new Breadcrumb({
      items: [{
        id: 'home',
        iconCss: 'e-icons e-home'
      }],
      enableNavigation: false,
      enableActiveItemNavigation: false,
      itemClick: (args) => {
        this.showPanel(args.item.index ?? 0);
      }
    });
    this.breadcrumb.appendTo(breadcrumbContainer);
  }
}
